import React, { useState } from "react";
import axios from "axios";
import config from "../../config";

const baseUrl = config.baseURL;

const T = {
  dark: "#2E4053",
  darkDeep: "#1C2A3D",
  muted: "#34495E",
  border: "#D1D8E0",
  surface: "#F4F6F9",
  white: "#FFFFFF",
  chipText: "#D1D8E0",
  green: "#16a34a",
  red: "#dc2626",
};

const inputStyle = {
  padding: "11px 14px",
  border: `1.5px solid ${T.border}`,
  borderRadius: 9,
  fontSize: 14,
  color: T.dark,
  outline: "none",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
  background: T.white,
};

const Field = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  children,
  select,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>
      {label}
      {required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}
    </label>
    {select ? (
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        style={{ ...inputStyle, background: T.white }}
      >
        {children}
      </select>
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        style={inputStyle}
      />
    )}
  </div>
);

const DemoPage = ({ currentPageData }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    number: "",
    company: "",
    team_size: "",
    industry: "",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const handle = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    console.log("Submitting demo request:", form);
    try {
      await axios.post(`${baseUrl}/api/contact_us/submit`, {
        ...form,
        message: `DEMO REQUEST — Industry: ${form.industry}, Team: ${form.team_size}. ${form.notes}`,
      });
      setStatus("success");
    } catch (err) {
      setErrMsg(
        err?.response?.data?.message || "Unable to submit. Please try again."
      );
      setStatus("error");
    }
  };

  return (
    <div
      style={{
        background: T.surface,
        minHeight: "100vh",
        fontFamily:
          "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}
    >
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, ${T.dark} 0%, ${T.darkDeep} 100%)`,
          padding: "72px 24px 80px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            color: T.chipText,
            marginBottom: 16,
          }}
        >
          FREE · NO COMMITMENT
        </div>
        <h1
          style={{
            fontSize: "clamp(28px,4vw,48px)",
            fontWeight: 900,
            color: T.white,
            margin: "0 0 16px",
            letterSpacing: -1,
          }}
        >
          Book a Personalized Demo
        </h1>
        <p
          style={{
            fontSize: 17,
            color: T.chipText,
            opacity: 0.75,
            maxWidth: 520,
            margin: "0 auto",
            lineHeight: 1.7,
          }}
        >
          See Spade ERP in action, tailored to your industry. A 30-minute live
          session with our team — no sales pressure.
        </p>
      </div>

      <div
        style={{
          maxWidth: 1060,
          margin: "0 auto",
          padding: "64px 24px",
          display: "grid",
          gridTemplateColumns: "1fr 460px",
          gap: 48,
          alignItems: "start",
        }}
      >
        {/* Left */}
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: T.dark,
              margin: "0 0 20px",
              letterSpacing: -0.5,
            }}
          >
            What to expect in your demo
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {[
              {
                n: "01",
                title: "Tailored walkthrough",
                desc: "We'll show you the features most relevant to your industry and team size.",
              },
              {
                n: "02",
                title: "Live Q&A session",
                desc: "Ask anything about features, pricing, integrations, or implementation.",
              },

              {
                n: "03",
                title: "Free trial activation",
                desc: "Walk away with a 14-day full-access trial set up and ready to go.",
              },
              //    {
              //   n: "04",
              //   title: "ETA & compliance setup",
              //   desc: "See how Spade handles Egyptian tax authority requirements out of the box.",
              // },
            ].map((s) => (
              <div
                key={s.n}
                style={{ display: "flex", gap: 16, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: T.dark,
                    color: T.white,
                    fontWeight: 800,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {s.n}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: T.dark,
                      marginBottom: 3,
                    }}
                  >
                    {s.title}
                  </div>
                  <div
                    style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.6 }}
                  >
                    {s.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 40,
              background: T.white,
              borderRadius: 14,
              border: `1px solid ${T.border}`,
              padding: "22px 24px",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: T.dark,
                marginBottom: 12,
              }}
            >
              ⏱️ Demo details
            </div>
            {[
              ["Duration", "30 minutes"],
              ["Format", "Video call (Google Meet / Zoom)"],
              ["Language", "Arabic or English"],
              ["Cost", "Completely free"],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13.5,
                  color: T.muted,
                  padding: "6px 0",
                  borderBottom: `1px solid ${T.surface}`,
                }}
              >
                <span>{k}</span>
                <strong style={{ color: T.dark }}>{v}</strong>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div
          style={{
            background: T.white,
            borderRadius: 18,
            border: `1px solid ${T.border}`,
            boxShadow: "0 4px 24px rgba(46,64,83,.08)",
            overflow: "hidden",
          }}
        >
          <div style={{ background: T.dark, padding: "22px 28px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.white }}>
              Request your demo
            </div>
            <div
              style={{
                fontSize: 13,
                color: T.chipText,
                opacity: 0.7,
                marginTop: 3,
              }}
            >
              We'll confirm within a few hours
            </div>
          </div>

          <div style={{ padding: "28px 28px 24px" }}>
            {status === "success" ? (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
                <h3
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: T.dark,
                    margin: "0 0 10px",
                  }}
                >
                  Demo Requested!
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: T.muted,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  Our team will contact you on <strong>{form.number}</strong> to
                  confirm your demo time. Check your email too!
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                  }}
                >
                  <Field
                    label="Full Name *"
                    name="full_name"
                    value={form.full_name}
                    onChange={handle}
                    placeholder="Ahmed Mohamed"
                    required
                  />
                  <Field
                    label="Phone *"
                    name="number"
                    value={form.number}
                    onChange={handle}
                    placeholder="+20 10 xxxx"
                    type="tel"
                    required
                  />
                </div>
                <Field
                  label="Email *"
                  name="email"
                  value={form.email}
                  onChange={handle}
                  placeholder="you@company.com"
                  type="email"
                  required
                />
                <Field
                  label="Company Name *"
                  name="company"
                  value={form.company}
                  onChange={handle}
                  placeholder="Your company"
                  required
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                  }}
                >
                  <Field
                    label="Team Size *"
                    name="team_size"
                    value={form.team_size}
                    onChange={handle}
                    required
                    select
                  >
                    <option value="">Select…</option>
                    {[
                      "1–5 employees",
                      "6–20 employees",
                      "21–50 employees",
                      "51–200 employees",
                      "200+ employees",
                    ].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </Field>
                  <Field
                    label="Industry *"
                    name="industry"
                    value={form.industry}
                    onChange={handle}
                    required
                    select
                  >
                    <option value="">Select…</option>
                    {[
                      "Retail",
                      "Wholesale & Distribution",
                      "Manufacturing",
                      "Healthcare",
                      "Cosmetics & Beauty",
                      "E-Commerce",
                      "Services",
                      "Other",
                    ].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </Field>
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <label
                    style={{ fontSize: 13, fontWeight: 600, color: T.muted }}
                  >
                    What do you want to see? (optional)
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handle}
                    placeholder="e.g. inventory management, HR payroll..."
                    rows={3}
                    style={{ ...inputStyle, resize: "vertical" }}
                  />
                </div>
                {errMsg && (
                  <div
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontSize: 13,
                      color: T.red,
                    }}
                  >
                    {errMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{
                    background: T.dark,
                    color: T.white,
                    border: "none",
                    borderRadius: 10,
                    padding: "14px",
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: "pointer",
                    opacity: status === "loading" ? 0.65 : 1,
                  }}
                >
                  {status === "loading" ? "Submitting…" : "Book My Free Demo →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
